import React, { useState, useEffect, useRef } from "react";
import { Play, Square, Plus, Trash2, Clock, Timer, ListChecks, Users, AlertTriangle, Check, History, ChevronDown, Sparkles, Loader2, X, Lock, Pencil, CalendarDays, BarChart3, Minus } from "lucide-react";
import { useApiData, apiFetch } from "../../lib/api.js";
import TimeKalendar from "./TimeKalendar.jsx";
import EditEntryModal from "../../components/EditEntryModal.jsx";
import { ACTIVITY_COLORS, QX_DESC, PUVOD_META, fmtDur, fmtHours, fmtDayCz, fmtHm, activitySolid } from "../../lib/timeMeta.js";
import { getCurrentUser, getRealUser } from "../../lib/auth.js";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import GradientHeader from "../../components/GradientHeader.jsx";
import { EmptyState } from "../../components/ui.jsx";
import StopUkolDialog from "../../components/StopUkolDialog.jsx";
import { toast } from "../../components/Toast.jsx";
import UkolPicker from "../../components/UkolPicker.jsx";
import MojeOdmena from "../../components/sales/MojeOdmena.jsx";

// === Time report v2 (14.7.2026) — osobní tracker měřicích rolí ===
// Činnosti přednastavené dle role (backend), kvadranty jen manager/csm (auto-default,
// klik na Qx u záznamu přepne), zpětné vkládání max 2 h, štítky původu záznamu
// (živě / zpětný start / vloženo zpětně), odrážky 3-5 povinné jen manager/csm
// se skládáním z časových bloků dne. Owner má velký přehled v Operations > Time tracking.

// Barvy, popisky kvadrantu a formatovace zijou v src/lib/timeMeta.js (sdili je
// kalendar i modal upravy). Re-export drzi zpetnou kompatibilitu importu
// z Operations > Time tracking.
export { ACTIVITY_COLORS, QX_DESC, PUVOD_META, fmtDur, fmtHours, fmtDayCz };

// Prepinac zalozek Dnes | Kalendar | Statistika. Sdileny zamerne: vlastni time
// tracking prihlaseneho cloveka i ownneruv read-only nahled na druheho cloveka
// (Operations > Time tracking > oko) musi vypadat i fungovat uplne stejne,
// aby nevznikly dve implementace, ktere se casem rozejdou.
export const TIME_TABS = [
  { key: "dnes", label: "Dnes", Icon: Clock },
  { key: "kalendar", label: "Kalendář", Icon: CalendarDays },
  { key: "statistika", label: "Statistika", Icon: BarChart3 },
];

export function TimeTabsSwitch({ tab, onTab, className = "" }) {
  return (
    <div className={`flex w-full shrink-0 gap-1 rounded-xl bg-slate-100 p-1 sm:inline-flex sm:w-auto ${className}`}>
      {TIME_TABS.map(({ key, label, Icon }) => (
        <button key={key} type="button" onClick={() => onTab(key)}
          className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-[13px] font-bold transition-colors sm:flex-none sm:justify-start sm:py-2 sm:text-sm ${
            tab === key ? "bg-white text-brand-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          <Icon className="w-4 h-4 shrink-0" /> {label}
        </button>
      ))}
    </div>
  );
}

// Report = 3-5 odrazek. V UI je kazda odrazka vlastni radek (prehlednejsi nez
// jedna textarea), do backendu jde dal text "- odrazka" po radcich.
const ODRAZKY_MIN = 3;
const ODRAZKY_MAX = 5;
function rowsFromText(t) {
  const r = String(t || "").split("\n").map((l) => l.replace(/^\s*[-*\u2022]\s*/, ""));
  while (r.length && !r[r.length - 1].trim()) r.pop();
  while (r.length < ODRAZKY_MIN) r.push("");
  return r;
}
function textFromRows(rows) {
  return (rows || []).map((l) => String(l).trim()).filter(Boolean).map((l) => `- ${l}`).join("\n");
}

// Sdílený obsah osobního trackeru. Použitý tady (self) i v owner náhledu
// (Operations > Time tracking > Zobrazit jako, readOnly=true, data přes ?as=email).
// onUpravitFond (1.11.2026, hlášení #189): jen ownerův náhled "Rozhraní" předává
// funkci, která otevře okno Upravit fond. Bez ní se tlačítko nekreslí.
export function TrackerBody({ data, readOnly = false, onAction, myTasks = [], onUpravitFond = null }) {
  const activities = data?.activities || [];
  const qxList = data?.qx || ["Q1", "Q2", "Q3", "Q4"];
  const useQuadrants = !!data?.useQuadrants;
  const bulletsRequired = !!data?.bulletsRequired;
  const running = data?.running || null;

  const [oblast, setOblast] = useState("");
  const [qx, setQx] = useState("");
  // 7.9.2026 (hlášení Michala): "Time tracking nejde spustit přes tlačítko nahoře".
  // Start je vypnutý, dokud není vybraná činnost — na počítači jsou štítky hned
  // nad tlačítkem a je to vidět, na telefonu je pod nimi a vypadá to jako mrtvé
  // tlačítko. Předvybíráme proto činnost z posledního dnešního záznamu (na tu
  // člověk skoro vždycky navazuje), a když žádný není a činnost je jen jedna,
  // vybere se ta. Zbytek řeší nápověda pod tlačítkem.
  React.useEffect(() => {
    if (oblast || readOnly || running) return;
    const posledni = [...(data?.todayEntries || [])]
      .filter((e) => e && e.oblast)
      .pop();
    const navrh = (posledni && posledni.oblast)
      || ((data?.activities || []).length === 1 ? data.activities[0].key : "");
    if (navrh) setOblast(navrh);
  }, [data, oblast, readOnly, running]);
  const [popis, setPopis] = useState("");
  const [ukolId, setUkolId] = useState("");
  const [backStart, setBackStart] = useState(false);
  const [minutesBack, setMinutesBack] = useState("");
  const [busy, setBusy] = useState(false);

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [running]);
  const elapsedSec = running ? (now - new Date(running.start_at).getTime()) / 1000 : 0;

  const [rows, setRows] = useState(() => rowsFromText(""));
  const [savingBullets, setSavingBullets] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  // 5.8.2026: report za VČEREJŠEK jde vyplnit do 10:00, ale UI ho dřív vůbec
  // neukazovalo (textarea byla vždy "dnes") — lidé neměli kde ho doplnit.
  // Teď se den přepíná záložkami a ukládá se s konkrétním datem.
  const [reportDen, setReportDen] = useState("dnes"); // "dnes" | "vcera"
  const prevDatum = data?.prevDatum || "";
  const prevEditable = !!data?.prevEditable;
  const prevChybi = prevEditable && !!data?.prevRequired && !data?.prevFilled;
  const denVcera = reportDen === "vcera" && prevEditable;
  const reportDatum = denVcera ? prevDatum : (data?.today || "");
  const savedText = denVcera ? String(data?.bulletsPrev || "") : String(data?.bulletsToday || "");
  useEffect(() => { if (data) setRows(rowsFromText(savedText)); }, [savedText, reportDen]);
  // Když včerejšek chybí a ještě jde doplnit, otevři rovnou jeho (jednou).
  const autoVceraRef = useRef(false);
  useEffect(() => {
    if (prevChybi && !readOnly && !autoVceraRef.current) { autoVceraRef.current = true; setReportDen("vcera"); }
  }, [prevChybi, readOnly]);
  const bulletsText = textFromRows(rows);
  const vyplneno = rows.filter((r) => r.trim()).length;
  const bulletsDirty = bulletsText.trim() !== textFromRows(rowsFromText(savedText)).trim();
  const setRow = (i, v) => setRows((r) => { const n = [...r]; n[i] = v; return n; });
  const addRow = () => setRows((r) => (r.length >= ODRAZKY_MAX ? r : [...r, ""]));
  const delRow = (i) => setRows((r) => (r.length <= ODRAZKY_MIN ? r.map((x, k) => (k === i ? "" : x)) : r.filter((_, k) => k !== i)));

  const [showManual, setShowManual] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmFill, setConfirmFill] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [mOblast, setMOblast] = useState(""); const [mMin, setMMin] = useState(""); const [mBack, setMBack] = useState(""); const [mPopis, setMPopis] = useState("");

  // post vrací true/false podle výsledku akce (onAction vrací false při chybě),
  // ať tlačítka můžou dát vizuální potvrzení "uloženo".
  const post = async (payload) => {
    if (readOnly) return false;
    setBusy(true);
    try { return await onAction(payload); }
    finally { setBusy(false); }
  };
  const start = () => {
    if (!oblast) { toast("Vyber činnost", "error"); return; }
    const p = { action: "start", oblast, popis };
    if (useQuadrants && qx) p.qx = qx;
    if (backStart && parseInt(minutesBack, 10) > 0) p.minutesBack = parseInt(minutesBack, 10);
    if (ukolId) { p.ukol_id = ukolId; p.ukol_nazev = myTasks.find((t) => t.id === ukolId)?.nazev || ""; }
    post(p).then(() => { setPopis(""); setQx(""); setBackStart(false); setMinutesBack(""); setUkolId(""); });
  };
  // Stop s navázaným úkolem: dialog "hotovo?" — Ano → označí úkol hotovo
  // (přes operations-ukoly, platí všechna pravidla: podúkoly, kontrola, projekt),
  // Ne → úkol se na backendu vrátí z Procesu do původního sloupce.
  const [stopUkolDialog, setStopUkolDialog] = useState(false);
  // 19.10.2026 (hlášení #166, #172): když měření běželo přes 4 h (zapomenuté
  // stopky), po zastavení se rovnou otevře úprava, ať ho člověk hned zkrátí.
  const [poStopuOtevrit, setPoStopuOtevrit] = useState(null);
  const oznacDlouheMereni = () => {
    if (running?.id && running?.start_at && Date.now() - new Date(running.start_at).getTime() > 4 * 3600 * 1000) {
      setPoStopuOtevrit(String(running.id));
    }
  };
  const stop = () => {
    if (running?.ukol_id) setStopUkolDialog(true);
    else { oznacDlouheMereni(); post({ action: "stop" }); }
  };
  const doStopWithUkol = async (markDone) => {
    setStopUkolDialog(false);
    if (markDone && running?.ukol_id) {
      try {
        const r = await apiFetch("/api/operations-ukoly", { method: "POST", body: JSON.stringify({ id: running.ukol_id, status: "hotovo" }) });
        const j = await r.json().catch(() => ({}));
        if (r.ok && j.keKontrole) toast(j.message || "Úkol má štítek kontrola - odevzdáno zadavateli ke schválení (dostal zprávu ve Slacku).");
        if (!r.ok) toast(`Úkol se nepodařilo označit jako hotový: ${j.error || "chyba"}. Měření se uloží a úkol se vrátí do původního sloupce.`, "error");
      } catch (e) {
        toast(`Úkol se nepodařilo označit jako hotový: ${e?.message || e}. Měření se uloží a úkol se vrátí do původního sloupce.`, "error");
      }
    }
    oznacDlouheMereni();
    post({ action: "stop" });
  };
  // Najdi záznam podle id v dnešku i včerejšku (měření přes půlnoc patří ke dni startu).
  const najdiZaznam = (id) => [...(data?.todayEntries || []), ...(data?.prevEntries || [])].find((e) => String(e.id) === String(id));
  useEffect(() => {
    if (!poStopuOtevrit || readOnly) return;
    const e = najdiZaznam(poStopuOtevrit);
    if (e) { setPoStopuOtevrit(null); if (e.editable || e.zkratitelne) setEditEntry(e); }
  }, [data, poStopuOtevrit]); // eslint-disable-line react-hooks/exhaustive-deps
  // Odkaz z Joky (?upravit=id) po automatickém vypnutí rovnou otevře úpravu.
  const odkazRef = useRef(false);
  useEffect(() => {
    if (!data || readOnly || odkazRef.current) return;
    const id = new URLSearchParams(window.location.search).get("upravit");
    if (!id) return;
    odkazRef.current = true;
    const e = najdiZaznam(id);
    if (e && (e.editable || e.zkratitelne)) setEditEntry(e);
    else toast("Tenhle záznam už upravit nejde, najdeš ho v záložce Kalendář.");
    try { const u = new URL(window.location.href); u.searchParams.delete("upravit"); window.history.replaceState(null, "", u.toString()); } catch (_) { /* nevadí */ }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  // Zrušení přes aplikační ConfirmDialog (ne nativní window.confirm), ať ladí s designem.
  const discard = () => setConfirmDiscard(true);
  const doDiscard = () => {
    setConfirmDiscard(false);
    post({ action: "discard" });
  };
  const del = (id) => post({ action: "deleteEntry", id });
  const addManual = () => {
    if (!mOblast || !mMin) { toast("Vyber činnost a minuty", "error"); return; }
    const p = { action: "manual", oblast: mOblast, minutes: mMin, popis: mPopis };
    if (parseInt(mBack, 10) > 0) p.minutesBack = parseInt(mBack, 10);
    post(p).then(() => { setMMin(""); setMPopis(""); setMBack(""); });
  };
  // Uložení reportu s viditelnou odezvou: tlačítko se přepne na "Ukládám…"
  // (spinner), po úspěchu na "Uloženo" + toast. Vedle je stav "Neuloženo",
  // dokud se text v textarea liší od uloženého reportu.
  const savedTimer = useRef(null);
  useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current); }, []);
  const saveBullets = async () => {
    if (savingBullets) return;
    setSavingBullets(true);
    let ok = false;
    try { ok = (await post({ action: "bullets", bullets: bulletsText, datum: reportDatum })) !== false; }
    finally { setSavingBullets(false); }
    if (!ok) return;
    setJustSaved(true);
    toast(denVcera ? `Report za ${fmtDayCz(prevDatum)} uložen` : "Report uložen");
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setJustSaved(false), 3000);
  };
  const setEntryQx = (id, x) => post({ action: "setQuadrant", id, qx: x });

  const todayEntries = data?.todayEntries || [];
  // Bloky, ze kterých se skládá report — dle vybraného dne (dnes / včera).
  const reportEntries = denVcera ? (data?.prevEntries || []) : todayEntries;
  const hoursByArea = data?.hoursByArea || {};
  // Dnešní součet z backendu je union intervalů — souběžné záznamy se nepočítají dvakrát.
  const rawSum = Object.values(hoursByArea).reduce((s, v) => s + v, 0);
  const totalSec = data?.totalTodaySec != null ? data.totalTodaySec : rawSum;
  const missing = bulletsRequired && (data?.missingEditable || []).length > 0;
  const selectedAct = activities.find((a) => a.key === oblast);
  const effectiveQx = qx || selectedAct?.defaultQx || "";

  // Skládání odrážek z jednolitých bloků dne: záznamy seřazené dle času,
  // klik na blok přidá řádek "- HH:MM-HH:MM Činnost: popis" do textarey.
  const addBlockToBullets = (e) => {
    const co = e.ukol_nazev || e.popis;
    const line = `${e.od || "?"}${e.do ? `-${e.do}` : ""} ${e.oblast}${co ? `: ${co}` : ""}`;
    setRows((r) => {
      const idx = r.findIndex((x) => !x.trim());
      if (idx >= 0) { const n = [...r]; n[idx] = line; return n; }
      return r.length >= ODRAZKY_MAX ? r : [...r, line];
    });
  };

  // Předvyplnění celého reportu z dnešních bloků: seskupí podle úkolu/činnosti
  // a sečte hodiny (ne surové časy od-do), ať draft rovnou čte jako report.
  // Zůstává draft — člověk doupraví formulaci a uloží.
  const fillFromTasks = () => {
    const groups = new Map();
    for (const e of reportEntries) {
      const co = String(e.ukol_nazev || e.popis || "").trim();
      const key = `${e.oblast}||${co}`;
      if (!groups.has(key)) groups.set(key, { oblast: e.oblast, co, sec: 0 });
      groups.get(key).sec += (e.duration_sec || 0);
    }
    const lines = [...groups.values()]
      .sort((a, b) => b.sec - a.sec)
      .map((g) => {
        const h = Math.round((g.sec / 3600) * 10) / 10;
        return `${g.oblast}${g.co ? `, ${g.co}` : ""} (${String(h).replace(".", ",")} h)`;
      })
      .slice(0, ODRAZKY_MAX);
    if (!lines.length) return;
    if (bulletsText.trim()) { setConfirmFill(lines); return; }
    setRows(rowsFromText(lines.join("\n")));
  };

  const Chip = ({ active, onClick, children, cls }) => (
    <button type="button" onClick={onClick} disabled={readOnly}
      className={`text-xs font-semibold px-3 py-1.5 sm:px-2.5 sm:py-1 rounded-full border transition ${active ? (cls || "bg-slate-800 text-white border-slate-800") : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"} ${readOnly ? "opacity-60 cursor-default" : ""}`}>
      {children}
    </button>
  );

  // Měsíční fond (manager + CSM, 20.8.2026; přepočet 27.8.2026). Fond není
  // pevné číslo, ale počet pracovních dnů v měsíci krát 7 h, přičemž státní
  // svátek se z pracovních dnů odečítá. Hlavní číslo je rozdíl proti poměrné
  // části fondu k dnešku — přesně to, co si Láďa dopočítával ručně, když si
  // chtěl napracovat hodiny.
  const fond = data?.mesicniFond || null;
  const svatkySlovy = (n) => {
    if (!n) return "";
    if (n === 1) return "1 svátek odečten";
    return n < 5 ? `${n} svátky odečteny` : `${n} svátků odečteno`;
  };

  return (
    <div className="space-y-5">
      {fond && (() => {
        // 21.9.2026 (varianta A): čtyři čísla nahoře, pod nimi výpočet, odkud se
        // "za měsíc má odpracovat" vzalo. Svátky i volno jsou odečtené z fondu.
        const napred = fond.rozdilHodin >= 0;
        const h = (x) => String(Math.round((Number(x) || 0) * 10) / 10).replace(".", ",");
        const nazevMesice = ["ledna", "února", "března", "dubna", "května", "června", "července", "srpna", "září", "října", "listopadu", "prosince"][parseInt(String(fond.mesic).slice(5, 7), 10) - 1] || "";
        const maOdpracovat = fond.maOdpracovatHodin ?? fond.fondHodin;
        const volnoH = Number(fond.volnoHodin) || 0;
        const svatkyTxt = (fond.svatkyVMesici || []).map((iso) => `${parseInt(iso.slice(8, 10), 10)}. ${parseInt(iso.slice(5, 7), 10)}.`).join(", ");
        // 1.11.2026 (hlášení #189): ruční úpravy fondu od ownera, každá jako pilulka ve výpočtu.
        const upravy = Array.isArray(fond.upravy) ? fond.upravy : [];
        const upravaH = Number(fond.upravaHodin) || 0;
        // Plný pruh = fond včetně úprav (odpracováno + volno). Bez úprav beze změny.
        const zakladPruhu = (fond.fondHodin || 0) + upravaH;
        const pct = (x) => `${Math.max(0, Math.min(100, (Number(x) / (zakladPruhu > 0 ? zakladPruhu : 1)) * 100))}%`;
        const Dlazdice = ({ label, children, hl }) => (
          <div className={`rounded-xl border bg-white px-3 py-2.5 flex flex-col gap-0.5 ${hl ? "border-brand-400" : "border-slate-200"}`}>
            <span className="text-xs text-slate-500">{label}</span>
            <span className="text-xl font-bold tabular-nums text-slate-900">{children}</span>
          </div>
        );
        return (
          <div className={`rounded-xl border p-4 space-y-3 ${napred ? "border-emerald-200 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
            <div className="flex items-center gap-2">
              <Timer className={`w-5 h-5 ${napred ? "text-emerald-600" : "text-amber-600"}`} />
              <span className="text-sm font-bold text-slate-900">Měsíční fond</span>
              {onUpravitFond && (
                <button type="button" onClick={onUpravitFond} className="btn btn-secondary btn-sm ml-auto">
                  <Pencil className="w-4 h-4" /> Upravit fond
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <Dlazdice label={`Za ${nazevMesice} má odpracovat`} hl>{h(maOdpracovat)} h</Dlazdice>
              <Dlazdice label="Odpracováno">{h(fond.odpracovanoHodin)} h</Dlazdice>
              <Dlazdice label="K dnešku">
                <span className={`inline-block rounded-full px-2.5 py-0.5 text-base ${napred ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>
                  {napred ? `+${h(fond.rozdilHodin)} h napřed` : `${h(Math.abs(fond.rozdilHodin))} h chybí`}
                </span>
              </Dlazdice>
              <Dlazdice label="Zbývá do konce měsíce">{h(fond.zbyvaDoFondu)} h</Dlazdice>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm tabular-nums">
              <span className="rounded-lg border border-slate-200 bg-white px-2 py-0.5">
                Fond {h(fond.fondHodin)} h ({fond.pracDnuCelkem} dnů × {fond.hodinNaDen || 7} h{svatkyTxt ? `, ${fond.svatkuVMesici === 1 ? "svátek" : "svátky"} ${svatkyTxt} odečten${fond.svatkuVMesici === 1 ? "" : "y"}` : ""})
              </span>
              {volnoH > 0 && (<>
                <span className="font-bold text-slate-400">−</span>
                <span className="rounded-lg bg-sky-100 px-2 py-0.5 text-sky-900">Dovolená a volno {h(volnoH)} h ({fond.volnoText})</span>
              </>)}
              {upravy.map((u) => (
                <React.Fragment key={u.id}>
                  <span className="font-bold text-slate-400">{u.hodin < 0 ? "−" : "+"}</span>
                  <span className="rounded-lg bg-plum-100 px-2 py-0.5 text-plum-900 [overflow-wrap:anywhere]">Úprava {h(Math.abs(u.hodin))} h{u.poznamka ? ` (${u.poznamka})` : ""}</span>
                </React.Fragment>
              ))}
              <span className="font-bold text-slate-400">=</span>
              <span className="rounded-lg bg-brand-100 px-2 py-0.5 font-bold text-brand-800">{h(maOdpracovat)} h</span>
            </div>
            <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-white"
              role="img" aria-label={`Odpracováno ${h(fond.odpracovanoHodin)} z ${h(maOdpracovat)} h, dovolená a volno ${h(volnoH)} h${upravaH ? `, úprava fondu ${upravaH > 0 ? "+" : "−"}${h(Math.abs(upravaH))} h` : ""}`}>
              <div className={`h-full ${napred ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: pct(fond.odpracovanoHodin) }} />
              {volnoH > 0 && <div className="h-full" style={{ width: pct(volnoH), background: "repeating-linear-gradient(45deg, #5AA9C9 0 6px, #E3F2F8 6px 10px)" }} />}
            </div>
            <div className="text-xs text-slate-500">
              {fond.zbyvaDnu > 0
                ? <>Zbývá {fond.zbyvaDnu} {fond.zbyvaDnu === 1 ? "pracovní den" : fond.zbyvaDnu < 5 ? "pracovní dny" : "pracovních dnů"}{fond.hodinDenne != null ? <>, to je asi <strong className="text-slate-700">{h(fond.hodinDenne)} h denně</strong></> : null}. </>
                : "Do konce měsíce už nezbývá žádný pracovní den. "}
              Odečítá se jen schválené celodenní volno.{upravy.length > 0 ? " Úpravu fondu zadal owner." : ""}
            </div>
          </div>
        );
      })()}
      {missing && !readOnly && (
        <button type="button" onClick={() => { if (prevEditable) setReportDen("vcera"); }}
          className="w-full text-left flex items-center gap-2 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-amber-800 hover:bg-amber-100 transition">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <span className="text-sm font-semibold">Nemáš vyplněný report za {(data.missingEditable || []).map(fmtDayCz).join(", ")}, doplň ho do 10:00, pak se den zamkne. Report je povinný každý pracovní den bez volna.{prevEditable ? " Klikni sem a doplň ho." : ""}</span>
        </button>
      )}

      {/* Stopky — pri behu hero karta (tmavy gradient), jinak klidna bila karta */}
      <div className={`rounded-2xl shadow-sm p-5 transition-colors ${running ? "bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 border border-brand-900 text-white" : "bg-white border border-slate-200"}`}>
        <div className="flex items-center gap-2 mb-3">
          <Clock className={`w-5 h-5 shrink-0 ${running ? "text-brand-200" : "text-brand-600"}`} />
          <h3 className={`text-[11px] uppercase tracking-wider font-bold ${running ? "text-white/70" : "text-slate-500"}`}>{running ? "Právě běží" : "Stopky"}</h3>
          <span className={`ml-auto text-sm ${running ? "text-white/70" : "text-slate-500"}`}>Dnes celkem <span className={`font-bold tabular-nums ${running ? "text-white" : "text-slate-800"}`}>{fmtHm(totalSec + (running ? elapsedSec : 0))}</span></span>
        </div>
        {running ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <span className="relative flex h-2.5 w-2.5 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
                </span>
                <div className="text-4xl sm:text-5xl font-extrabold tabular-nums text-white tracking-tight">{fmtDur(elapsedSec)}</div>
              </div>
              <div className="text-sm text-white/80 mt-2 flex items-center gap-2 flex-wrap">
                {/* Překlik při startu? Činnost jde přepnout ZA BĚHU — select stylovaný jako pill (3.8.2026). */}
                {!readOnly ? (
                  <label className={`inline-flex items-center gap-0.5 text-xs font-semibold pl-2 pr-1 py-0.5 rounded-full cursor-pointer ${ACTIVITY_COLORS[running.oblast] || "bg-slate-100 text-slate-700"}`}
                    title="Překlikl(a) ses? Tady změníš činnost běžícího měření, čas běží dál.">
                    <select value={running.oblast} disabled={busy}
                      onChange={(e) => post({ action: "updateRunning", oblast: e.target.value })}
                      className="bg-transparent appearance-none cursor-pointer focus:outline-none font-semibold">
                      {activities.map((a) => <option key={a.key} value={a.key}>{a.key}</option>)}
                      {!activities.some((a) => a.key === running.oblast) && <option value={running.oblast}>{running.oblast}</option>}
                    </select>
                    <ChevronDown className="w-3 h-3 opacity-60 shrink-0" />
                  </label>
                ) : (
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ACTIVITY_COLORS[running.oblast] || "bg-slate-100 text-slate-700"}`}>{running.oblast}</span>
                )}
                {running.qx && <span className="text-[11px] font-bold text-white bg-white/15 rounded px-1.5 py-0.5" title={QX_DESC[running.qx]}>{running.qx}</span>}
                {running.puvod === "zpetny_start" && <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded border ${PUVOD_META.zpetny_start.cls}`}>{PUVOD_META.zpetny_start.label}</span>}
                {running.ukol_nazev && <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-200"><ListChecks className="w-3.5 h-3.5" /> {running.ukol_nazev}</span>}
                {running.popis && (
                  <span className="inline-flex items-center gap-2 text-white/70">
                    <span aria-hidden className="w-px h-3 bg-current opacity-40" />
                    {running.popis}
                  </span>
                )}
              </div>
            </div>
            {!readOnly && (
              <div className="flex flex-col gap-1.5 w-full sm:w-auto">
                <button onClick={stop} disabled={busy} className="btn btn-danger w-full sm:w-auto">
                  <Square className="w-4 h-4" /> Zastavit a uložit
                </button>
                <button onClick={discard} disabled={busy} className="btn btn-na-tmavem">
                  <Trash2 className="w-3.5 h-3.5" /> Zrušit bez uložení
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1.5">Činnost</div>
              {/* 7.9.2026: na telefonu deset štítků zabralo půl obrazovky a Start
                  byl až pod nimi — vypadal jako rozbitý. Tam je proto rozbalovací
                  seznam, který telefon vykreslí svým vlastním kolečkem. Na širokou
                  obrazovku se štítky vejdou vedle sebe a zůstávají. */}
              <select value={oblast} onChange={(e) => { setOblast(e.target.value); setQx(""); }}
                className="field sm:hidden w-full font-semibold">
                <option value="">Vyber činnost…</option>
                {activities.map((a) => <option key={a.key} value={a.key}>{a.key}</option>)}
              </select>
              <div className="hidden sm:flex flex-wrap gap-1.5">
                {activities.map((a) => <Chip key={a.key} active={oblast === a.key} cls={(ACTIVITY_COLORS[a.key] || "") + " border-transparent ring-2 ring-slate-300"} onClick={() => { setOblast(a.key); setQx(""); }}>{a.key}</Chip>)}
              </div>
            </div>
            {useQuadrants && oblast && (
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1.5">Kvadrant (auto: {selectedAct?.defaultQx || "—"}, klikem změníš)</div>
                <div className="flex flex-wrap gap-1.5">
                  {qxList.map((x) => (
                    <Chip key={x} active={effectiveQx === x} onClick={() => setQx(x)}>
                      <span className="font-bold">{x}</span>
                      <span aria-hidden className="inline-block align-middle w-px h-3 bg-current opacity-40 mx-1.5" />
                      <span className="font-normal opacity-80">{QX_DESC[x]}</span>
                    </Chip>
                  ))}
                </div>
              </div>
            )}
            {!readOnly && (<>
              {myTasks.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 mb-1.5">Úkol z Operations (volitelně), startem se úkol přesune do Procesu, po zastavení se vrátí, pokud ho neoznačíš hotový</div>
                  <UkolPicker tasks={myTasks} value={ukolId} onChange={setUkolId} />
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <input value={popis} onChange={(e) => setPopis(e.target.value)} placeholder="Na čem děláš (volitelně)…"
                  className="field flex-1" />
                {/* Vypnuté tlačítko rovnou říká, co po tobě chce — šedý "Start"
                    bez vysvětlení vypadal jako chyba aplikace. */}
                <button onClick={start} disabled={busy || !oblast} className="btn btn-primary w-full sm:w-auto">
                  {oblast ? <><Play className="w-4 h-4" /> Start</> : "Vyber činnost"}
                </button>
              </div>
              {!oblast && (
                <div className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  Bez činnosti nejde měření spustit, vyber ji nahoře.
                </div>
              )}
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                <input type="checkbox" checked={backStart} onChange={(e) => setBackStart(e.target.checked)} className="w-3.5 h-3.5 accent-brand-600" />
                <History className="w-3.5 h-3.5 text-slate-400" /> Začal(a) jsem už dřív (záznam dostane štítek "zpětný start")
              </label>
              {backStart && (
                <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-300 rounded-xl px-3 py-2">
                  <span className="text-xs font-semibold text-amber-800">před</span>
                  <input type="number" min="1" max="120" value={minutesBack} onChange={(e) => setMinutesBack(e.target.value)}
                    placeholder="30" className="field w-16 text-center" />
                  <span className="text-xs font-semibold text-amber-800">minutami (max 120)</span>
                </div>
              )}
            </>)}
          </div>
        )}
      </div>

      {/* Dnešní záznamy + součty */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="flex items-center flex-wrap gap-2 mb-3">
          <ListChecks className="w-5 h-5 text-slate-500" /><h3 className="text-base font-bold text-slate-900 flex-1 min-w-0">Dnešní záznamy</h3>
          <span className="text-sm font-bold tabular-nums text-slate-700">{fmtHours(totalSec)}</span>
          {!readOnly && (
            <button onClick={() => setShowManual((s) => !s)}
              className={`inline-flex items-center gap-1 text-xs font-semibold rounded-xl px-2.5 py-1 border transition-colors ${showManual ? "text-slate-600 bg-slate-100 border-slate-300 hover:bg-slate-200" : "text-brand-700 bg-brand-50 border-brand-200 hover:bg-brand-100"}`}>
              {showManual ? <><X className="w-3.5 h-3.5" /> Zavřít</> : <><Plus className="w-3.5 h-3.5" /> Doplnit zpětně</>}
            </button>
          )}
        </div>
        {showManual && !readOnly && (
          <div className="flex flex-wrap items-center gap-2 mb-3 p-2.5 bg-amber-50/60 border border-amber-200 rounded-xl">
            {/* 5.8.2026: panel sel otevrit, ale ne zavrit — krizek primo v nem. */}
            <div className="w-full flex items-start gap-2">
              <span className="flex-1 text-[11px] text-amber-800 font-semibold">Zpětně jde vložit jen posledních 120 minut. Záznam dostane štítek "vloženo zpětně".</span>
              <button type="button" onClick={() => setShowManual(false)} title="Zavřít"
                className="shrink-0 -mt-0.5 -mr-0.5 p-1 rounded text-amber-700 hover:text-amber-900 hover:bg-amber-100"><X className="w-4 h-4" /></button>
            </div>
            <select value={mOblast} onChange={(e) => setMOblast(e.target.value)} className="field w-auto">
              <option value="">Činnost…</option>{activities.map((a) => <option key={a.key} value={a.key}>{a.key}</option>)}
            </select>
            <span className="inline-flex items-center gap-1 text-xs text-slate-600">začátek před
              <input type="number" min="1" max="120" value={mBack} onChange={(e) => setMBack(e.target.value)} placeholder="60" title="Začátek: před kolika minutami (volitelně, jinak se dopočítá od délky)" className="field w-16 text-center" /> min</span>
            <span className="inline-flex items-center gap-1 text-xs text-slate-600">délka
              <input type="number" min="1" max="120" value={mMin} onChange={(e) => setMMin(e.target.value)} placeholder="min" className="field w-16 text-center" /> min</span>
            <input value={mPopis} onChange={(e) => setMPopis(e.target.value)} placeholder="popis" className="field w-full sm:flex-1 sm:w-auto min-w-[120px]" />
            <button onClick={addManual} disabled={busy} className="btn btn-primary w-full sm:w-auto">Přidat</button>
          </div>
        )}
        {todayEntries.length === 0 ? (
          <div className="text-sm text-slate-400 py-3 text-center">Zatím žádný záznam dnes.</div>
        ) : (
          <div className="divide-y divide-slate-50">
            {todayEntries.map((e) => {
              const pv = PUVOD_META[e.puvod] || PUVOD_META.live;
              return (
                <div key={e.id} className="flex items-center gap-2 py-2 flex-wrap">
                  <span className="text-xs text-slate-400 tabular-nums shrink-0 w-20">{e.od || "?"}{e.do ? `-${e.do}` : ""}</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${ACTIVITY_COLORS[e.oblast] || "bg-slate-100 text-slate-700"}`}>{e.oblast}</span>
                  {useQuadrants && (
                    <span className="inline-flex gap-0.5 shrink-0">
                      {["Q1", "Q2", "Q3", "Q4"].map((x) => (
                        <button key={x} disabled={readOnly} onClick={() => setEntryQx(e.id, x)} title={QX_DESC[x]}
                          className={`text-[11px] font-bold px-2 py-1 sm:px-1 sm:py-0.5 rounded ${e.qx === x ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-400 hover:text-slate-600"} ${readOnly ? "cursor-default" : ""}`}>
                          {x}
                        </button>
                      ))}
                    </span>
                  )}
                  <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded border shrink-0 ${pv.cls}`} title={pv.title}>{pv.label}</span>
                  {e.ukol_nazev && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-700 bg-brand-50 border border-brand-200 rounded px-1.5 py-0.5 shrink-0 max-w-[180px]" title={`Úkol: ${e.ukol_nazev}`}>
                      <ListChecks className="w-3 h-3 shrink-0" /> <span className="truncate">{e.ukol_nazev}</span>
                    </span>
                  )}
                  {e.popis
                    ? <span className="text-sm text-slate-700 flex-1 truncate min-w-[80px]">{e.popis}</span>
                    : <span className="flex-1" />}
                  <span className="text-sm font-semibold tabular-nums text-slate-800 shrink-0">{fmtHours(e.duration_sec)}</span>
                  {!readOnly && (e.editable || e.zkratitelne) && (
                    <button onClick={() => setEditEntry(e)} className="text-slate-300 hover:text-brand-600 shrink-0 p-1.5 -m-1.5 sm:p-0 sm:m-0 sm:mr-1"
                      title="Upravit záznam, do 2 h od konce vše, do 7 dnů jde měření zkrátit" aria-label="Upravit záznam"><Pencil className="w-4 h-4" /></button>
                  )}
                  {!readOnly && e.deletable && (
                    <button onClick={() => del(e.id)} className="text-slate-300 hover:text-rose-500 shrink-0 p-1.5 -m-1.5 sm:p-0 sm:m-0" title="Smazat (jde jen do 2 h od konce)"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {totalSec > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
            {/* Rozpad dne na jeden pruh — hned je videt, cemu sel cas. */}
            <div className="flex h-2 w-full rounded-full overflow-hidden bg-slate-100">
              {Object.entries(hoursByArea).sort((a, b) => b[1] - a[1]).map(([a, sec]) => (
                <div key={a} className={activitySolid(a)} style={{ width: `${(sec / totalSec) * 100}%` }} title={`${a}: ${fmtHours(sec)}`} />
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(hoursByArea).sort((a, b) => b[1] - a[1]).map(([a, sec]) => (
                <span key={a} className={`text-xs font-semibold px-2 py-1 rounded-full ${ACTIVITY_COLORS[a] || "bg-slate-100 text-slate-700"}`}>{a}: {fmtHours(sec)}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Odrážky — jen role s povinným reportem (manager, csm) */}
      {bulletsRequired && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between [&>*]:min-w-0 gap-2 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <Check className="w-5 h-5 text-emerald-600 shrink-0" />
              <h3 className="text-base font-bold text-slate-900 [overflow-wrap:anywhere]">
                Čemu jsem věnoval(a) pozornost: {denVcera ? fmtDayCz(prevDatum) : `dnes ${fmtDayCz(data?.today)}`}
              </h3>
            </div>
            <span className={`shrink-0 self-start sm:self-auto text-xs font-bold px-2.5 py-1 rounded-full border ${vyplneno >= ODRAZKY_MIN ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
              {vyplneno} z {ODRAZKY_MIN}–{ODRAZKY_MAX} odrážek
            </span>
          </div>
          {/* Přepínač dne — včerejšek jde doplnit do 10:00 (pak ho backend zamkne). */}
          {!readOnly && (
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <button type="button" onClick={() => setReportDen("dnes")}
                className={`text-xs font-bold px-3 py-1.5 rounded-full border transition ${!denVcera ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                Dnes {fmtDayCz(data?.today)} {data?.bulletsToday ? "✓" : ""}
              </button>
              <button type="button" disabled={!prevEditable} onClick={() => prevEditable && setReportDen("vcera")}
                title={prevEditable ? "Doplnit report za včerejšek" : "Včerejšek je po 10:00 zamčený"}
                className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full border transition ${!prevEditable ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed" : denVcera ? "bg-amber-500 text-white border-amber-500" : (prevChybi ? "bg-amber-50 text-amber-800 border-amber-300 hover:border-amber-400" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400")}`}>
                {!prevEditable && <Lock className="w-3 h-3" />}
                Včera {fmtDayCz(prevDatum)} {data?.prevFilled ? "✓" : (prevChybi ? "(chybí)" : "")}
              </button>
              <span className="text-[11px] text-slate-500">
                {prevEditable
                  ? "Report se ukládá zvlášť za každý den. Včerejšek doplň jen do dnešních 10:00."
                  : "Report se ukládá zvlášť za každý den. Včerejšek je po 10:00 zamčený, příště ho doplň tady na záložce „Včera“ do 10:00."}
              </span>
            </div>
          )}
          {reportEntries.length > 0 && !readOnly && (
            <div className="mb-2">
              <button onClick={fillFromTasks}
                className="btn btn-primary mb-2"
                title="Předvyplní report z úkolů vybraného dne, seskupí podle úkolu a sečte hodiny. Zůstává draft, doupravíš a uložíš.">
                <Sparkles className="w-3.5 h-3.5" /> {denVcera ? "Předvyplnit z včerejších úkolů" : "Předvyplnit z dnešních úkolů"}
              </button>
              <div className="text-xs text-slate-500 mb-1">…nebo klikni na jednotlivý blok dne a vloží se jako odrážka:</div>
              <div className="flex flex-wrap gap-1.5">
                {reportEntries.map((e) => (
                  <button key={e.id} onClick={() => addBlockToBullets(e)}
                    className="text-[11px] font-semibold px-2 py-1 rounded-xl border border-slate-200 bg-slate-50 hover:bg-brand-50 hover:border-brand-300 text-slate-600">
                    {e.od || "?"}{e.do ? `-${e.do}` : ""} {e.oblast}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className={`mt-2 w-6 h-6 shrink-0 rounded-full text-[11px] font-bold flex items-center justify-center ${r.trim() ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{i + 1}</span>
                <textarea
                  value={r} readOnly={readOnly} rows={1}
                  onChange={(ev) => setRow(i, ev.target.value.replace(/\n/g, " "))}
                  onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); if (i === rows.length - 1) addRow(); } }}
                  onInput={(ev) => { ev.target.style.height = "auto"; ev.target.style.height = `${ev.target.scrollHeight}px`; }}
                  placeholder={i === 0 ? "Čemu jsi věnoval(a) pozornost…" : "Další odrážka…"}
                  className="flex-1 min-w-0 text-sm border border-slate-300 rounded-xl px-3 py-2.5 resize-none leading-snug focus:outline-none focus:border-emerald-400 [overflow-wrap:anywhere]" />
                {!readOnly && (
                  <button type="button" onClick={() => delRow(i)} title="Vymazat odrážku"
                    className="mt-1.5 shrink-0 p-1.5 rounded-xl text-slate-300 hover:text-rose-500 hover:bg-rose-50"><Minus className="w-4 h-4" /></button>
                )}
              </div>
            ))}
            {!readOnly && rows.length < ODRAZKY_MAX && (
              <button type="button" onClick={addRow}
                className="btn btn-secondary border-dashed">
                <Plus className="w-3.5 h-3.5" /> Přidat odrážku
              </button>
            )}
          </div>
          {!readOnly && (
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 mt-2">
              <span className={`text-xs ${denVcera ? "font-semibold text-amber-600" : "text-slate-400"}`}>
                {denVcera ? "Uzávěrka DNES v 10:00, pak se den zamkne. Minimálně 3 odrážky." : "Uzávěrka: další den v 10:00. Minimálně 3 odrážky."}
              </span>
              <div className="flex items-center justify-end gap-2">
                {savingBullets ? (
                  <span className="text-xs font-semibold text-slate-500">Ukládám…</span>
                ) : justSaved ? (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600"><Check className="w-3.5 h-3.5" /> Uloženo</span>
                ) : bulletsDirty ? (
                  <span className="text-xs font-bold text-amber-600">Neuloženo</span>
                ) : bulletsText.trim() ? (
                  <span className="text-xs font-semibold text-slate-400">Uloženo</span>
                ) : null}
                <button onClick={saveBullets} disabled={busy || savingBullets || (bulletsText.trim() ? vyplneno < ODRAZKY_MIN : false)}
                  title={bulletsText.trim() && vyplneno < ODRAZKY_MIN ? `Vyplň aspoň ${ODRAZKY_MIN} odrážky` : ""}
                  className={`btn w-full text-white sm:w-auto ${
                    justSaved ? "bg-emerald-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
                  {savingBullets ? <><Loader2 className="w-4 h-4 animate-spin" /> Ukládám…</>
                    : justSaved ? <><Check className="w-4 h-4" /> Uloženo</>
                    : denVcera ? `Uložit report za ${fmtDayCz(prevDatum)}` : "Uložit report"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {!bulletsRequired && readOnly && data?.bulletsToday && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
          <h3 className="text-base font-bold text-slate-900 mb-2">Denní report</h3>
          <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{data.bulletsToday}</pre>
        </div>
      )}

      {/* Dialog při zastavení měření s navázaným úkolem: checklist podúkolů
          přímo v dialogu, "Hotovo" se odemkne až po odškrtnutí všech. */}
      <StopUkolDialog
        open={stopUkolDialog}
        ukolId={running?.ukol_id}
        ukolNazev={running?.ukol_nazev}
        busy={busy}
        onDone={() => doStopWithUkol(true)}
        onNotDone={() => doStopWithUkol(false)}
        onClose={() => setStopUkolDialog(false)}
      />

      <ConfirmDialog
        open={!!confirmFill}
        title="Přepsat rozepsaný report?"
        description={`Report se nahradí draftem z ${denVcera ? "včerejších" : "dnešních"} úkolů. Rozepsaný text se ztratí.`}
        confirmLabel="Přepsat draftem"
        variant="warning"
        onConfirm={() => { setRows(rowsFromText((confirmFill || []).join("\n"))); setConfirmFill(null); }}
        onCancel={() => setConfirmFill(null)}
      />

      {/* Uprava zaznamu: zacatek se nemeni, do 2 h od konce vse, do 7 dnu jen zkratit. */}
      <EditEntryModal
        open={!!editEntry}
        entry={editEntry}
        activities={activities}
        useQuadrants={useQuadrants}
        canDelete={!!editEntry?.deletable}
        busy={busy}
        onSave={(payload) => post(payload)}
        onDelete={async () => { const id = editEntry?.id; setEditEntry(null); if (id) await post({ action: "deleteEntry", id }); }}
        onClose={() => setEditEntry(null)}
      />

      <ConfirmDialog
        open={confirmDiscard}
        title="Zrušit běžící měření?"
        description="Rozdělané měření se zahodí bez uložení. Nejde to vrátit."
        confirmLabel="Zrušit měření"
        variant="danger"
        onConfirm={doDiscard}
        onCancel={() => setConfirmDiscard(false)}
      />

      {/* Historie 14 dní */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <h3 className="text-base font-bold text-slate-900 mb-2">Posledních 14 dní</h3>
        <div className="grid grid-cols-2 sm:grid-cols-7 gap-1.5">
          {(data?.list || []).slice().reverse().map((d) => (
            <div key={d.datum} className={`rounded-xl border p-2 text-center ${
              d.volno ? "bg-sky-50 border-sky-200"
              : d.filled ? "bg-emerald-50 border-emerald-200"
              : d.required ? (d.editable ? "bg-amber-50 border-amber-200" : "bg-rose-50 border-rose-200")
              : "bg-slate-50 border-slate-200"}`}>
              <div className="text-[11px] text-slate-500">{fmtDayCz(d.datum)}</div>
              <div className="text-sm font-bold tabular-nums text-slate-800">{d.hours} h</div>
              {bulletsRequired && (
                <div className="text-[11px] font-semibold">
                  {d.volno ? (d.hours > 0 ? "práce během volna" : "volno") : d.filled ? "✓ report" : d.required ? (d.editable ? "chybí" : "nevyplněno") : "—"}
                </div>
              )}
              {d.volnoHodiny && (
                <div className="text-[11px] font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1 mt-0.5">Volno {d.volnoHodiny}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function TimeReport() {
  const role = String(getCurrentUser()?.role || "").toLowerCase();
  const realRole = String(getRealUser()?.role || "").toLowerCase();
  const showTracker = role !== "owner";              // owner čas neměří (velký přehled má v Operations)
  const showOverview = role === "owner" || role === "manager";
  // Owner v "Náhled jako": backend běží pod ownerem → roleHint ukáže presety dané role.
  const roleHint = realRole === "owner" && role !== "owner" ? `?roleHint=${role}` : "";
  const { data, refetch } = useApiData(showTracker ? `/api/time-report${roleHint}` : null);
  const { data: ov, refetch: refetchOv } = useApiData(showOverview ? "/api/time-report?overview=1" : null);
  // Moje otevřené úkoly z Operations pro napojení bloků měření (co blok, to úkol). Jen manager/csm.
  const canLinkTasks = role === "manager" || role === "csm";
  const { data: ukolyData } = useApiData(showTracker && canLinkTasks ? "/api/operations-ukoly?mine=1" : null);
  const myTasks = (ukolyData?.tasks || []).filter((t) => ["zasobnik", "prioritni", "mesicni", "tydenni", "v_procesu"].includes(t.status));
  // Zalozky: Dnes (stopky + report) | Kalendar (tyden/mesic) | Statistika.
  const [tab, setTab] = useState("dnes");

  // Když stopky v horní liště něco změní, obnov data i tady (a naopak widget poslouchá náš event).
  useEffect(() => {
    const onChanged = () => { refetch?.(); if (showOverview) refetchOv?.(); };
    window.addEventListener("fw40-time-changed", onChanged);
    return () => window.removeEventListener("fw40-time-changed", onChanged);
  }, [refetch, refetchOv, showOverview]);

  const onAction = async (payload) => {
    try {
      // Owner v náhledu: backend validuje činnosti dle náhledové role.
      const body = roleHint ? { ...payload, roleHint: role } : payload;
      const r = await apiFetch("/api/time-report", { method: "POST", body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Akce selhala");
      await refetch();
      if (showOverview) refetchOv?.();
      window.dispatchEvent(new Event("fw40-time-changed"));
      return true;
    } catch (e) { toast("Chyba: " + (e?.message || e), "error"); return false; }
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 px-4 py-4 sm:space-y-6 sm:px-5 sm:py-6">
      {/* 12.9.2026 (fáze 4c): ruční nadpis nahradila společná hlavička stránky.
          Věta pod nadpisem se schovala pod otazník u ikony, stejně jako všude jinde. */}
      <GradientHeader
        title={role === "sales" ? "Report" : "Time report"}
        icon={Timer}
        napoveda={showTracker
          ? (tab === "kalendar" ? "Tvůj čas v kalendáři, klikni na blok a upravíš ho podle pravidel."
            : tab === "statistika" ? "Kam ti čas reálně teče, po činnostech, kvadrantech a úkolech."
            : (data?.bulletsRequired
              ? "Měř si práci stopkami (i v horní liště) a večer vyber 3 až 5 odrážek, čemu jsi věnoval pozornost."
              : "Měř si práci stopkami, i tlačítkem v horní liště. Zpětně jde doplnit max 2 hodiny."))
          : "Přehled denních time reportů týmu."}
      />
      {/* 30. 9. 2026: sales vidí svou odměnu (hodiny krát sazba a bonusy za klientky), mockup v8. */}
      {/* 1. 10. 2026: manažer přepnutý do sales odměnu sales nemá, platí mu manažerská výplata. */}
      {role === "sales" && getRealUser()?.role !== "manager" && <MojeOdmena />}
      {showTracker && <TimeTabsSwitch tab={tab} onTab={setTab} />}

      {showTracker && tab === "dnes" && <TrackerBody data={data} onAction={onAction} myTasks={myTasks} />}
      {/* Kalendar i statistika je jedna komponenta na jednom miste v JSX,
          takze prepnuti zalozky nezahodi vybrane obdobi. */}
      {showTracker && tab !== "dnes" && (
        <TimeKalendar mode={tab} roleHint={realRole === "owner" && role !== "owner" ? role : ""} />
      )}

      {/* Přehled (owner/manager) */}
      {showOverview && ov && (
        <div className="glass rounded-xl p-4">
          <div className="mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-slate-500" /><h3 className="text-base font-bold text-slate-900">Přehled týmu (7 dní)</h3></div>

          {/* Na telefonu řádky, na počítači tabulka (rozhodnutí T1). */}
          <div className="divide-y divide-slate-200 sm:hidden">
            {(ov.rows || []).map((r) => (
              <div key={r.email} className="py-3">
                <div className="flex items-start justify-between gap-2">
                  <span className="min-w-0 flex-1 text-sm font-bold text-slate-900">{r.name}</span>
                  <span className="shrink-0 text-base font-bold tabular-nums text-slate-900">{r.totalHours} h</span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {r.days.slice().reverse().map((d) => (
                    <span
                      key={d.datum}
                      className={`rounded-lg px-2 py-0.5 text-xs font-semibold ${
                        d.filled ? "bg-emerald-50 text-emerald-800"
                          : d.missing ? "bg-rose-50 text-rose-800"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {fmtDayCz(d.datum)} {d.hours > 0 ? `${d.hours} h` : "—"}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="hidden overflow-x-auto sm:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-400">
                <th className="text-left font-semibold py-1 pr-3">Osoba</th>
                {(ov.days || []).slice().reverse().map((d) => <th key={d} className="text-center font-semibold px-1">{fmtDayCz(d)}</th>)}
                <th className="text-right font-semibold pl-2">Σ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {(ov.rows || []).map((r) => (
                <tr key={r.email}>
                  <td className="py-1.5 pr-3 font-semibold text-slate-800 truncate">{r.name}</td>
                  {r.days.slice().reverse().map((d) => (
                    <td key={d.datum} className="text-center px-1">
                      <div className={`text-xs font-bold ${d.filled ? "text-emerald-600" : d.missing ? "text-rose-500" : "text-slate-400"}`}>{r.bulletsRequired ? (d.filled ? "✓" : d.missing ? "✗" : "—") : "—"}</div>
                      <div className="text-[11px] text-slate-400 tabular-nums">{d.hours > 0 ? `${d.hours}h` : ""}</div>
                    </td>
                  ))}
                  <td className="py-1.5 pl-2 text-right font-bold tabular-nums text-slate-800">{r.totalHours} h</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {(!ov.rows || ov.rows.length === 0) && (
            <EmptyState icon={Users} title="Žádní back-office lidé" hint="Time report vyplňují jen lidé mimo trenérské pozice." />
          )}
        </div>
      )}
    </div>
  );
}
